import { useEffect, useState } from "react";
import { useParams, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { RTL_LOCALES, type SupportedLocale } from "../../lib/i18n";
import { pickPreferredLang } from "../../lib/detect-language";
import { switchLocale } from "./language-switcher";
import {
  onAnalyticsConsent,
  readAnalyticsConsent,
} from "../consent/analytics-consent";

import { Icon } from "@majourneys/icons";
const DISMISS_KEY = "mj.langBannerDismissed";

const LABELS: Record<SupportedLocale, string> = {
  en: "English",
  fr: "Français",
  ar: "العربية",
  es: "Español",
  it: "Italiano",
  de: "Deutsch",
  nl: "Nederlands",
};

/**
 * Slim, dismissible banner that suggests the visitor's browser-preferred
 * language when it differs from the language in the URL. Renders only in the
 * browser (never during SSR) so crawlers always see the untouched page — no
 * redirect, no SEO impact. The choice to switch reuses the canonical
 * `switchLocale` URL logic; dismissal is remembered so it never nags.
 */
export function LanguageSuggestionBanner() {
  const { i18n } = useTranslation();
  const { lang } = useParams({ strict: false }) as { lang?: SupportedLocale };
  const location = useRouterState({ select: (s) => s.location });
  const [target, setTarget] = useState<SupportedLocale | null>(null);
  // The cookie banner shows until consent is answered; holding this one back
  // until then keeps the two from stacking on top of each other.
  const [cookieAnswered, setCookieAnswered] = useState(false);

  useEffect(() => {
    if (readAnalyticsConsent() !== null) setCookieAnswered(true);
    return onAnalyticsConsent(() => setCookieAnswered(true));
  }, []);

  const current = lang ?? "en";

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(DISMISS_KEY)) return;
    const suggestion = pickPreferredLang(navigator.languages, current);
    setTarget(suggestion);
  }, [current]);

  if (!target || !cookieAnswered) return null;

  const search = typeof location.search === "string" ? location.search : "";
  const hash = location.hash ? `#${location.hash}` : "";
  const href = location.href ?? `${location.pathname}${location.searchStr ?? search}${hash}`;
  const isRtl = RTL_LOCALES.includes(target as any);

  const dismiss = () => {
    if (typeof window !== "undefined") window.localStorage.setItem(DISMISS_KEY, "1");
    setTarget(null);
  };

  return (
    <div
      role="region"
      aria-label={i18n.t("langSuggestion.aria", { lng: target, defaultValue: "Language suggestion" })}
      dir={isRtl ? "rtl" : "ltr"}
      lang={target}
      className="fixed inset-x-3 bottom-[calc(96px+env(safe-area-inset-bottom,0px))] z-[60] mx-auto flex max-w-md items-center gap-3 rounded-2xl border border-black/5 bg-white px-4 py-3 shadow-lg md:bottom-4 md:start-4 md:mx-0"
    >
      <span className="flex-1 text-[13px] leading-snug text-[#5a5d77]">
        {i18n.t("langSuggestion.prompt", {
          lng: target,
          defaultValue: `View this page in ${LABELS[target]}?`,
        })}
      </span>
      <a
        href={switchLocale(href, target)}
        onClick={dismiss}
        className="inline-flex min-h-[36px] items-center rounded-full bg-[#110E39] navy-depth px-4 py-1.5 text-[13px] font-bold text-white"
      >
        {i18n.t("langSuggestion.switch", { lng: target, defaultValue: "Switch" })}
      </a>
      <button
        type="button"
        onClick={dismiss}
        aria-label={i18n.t("langSuggestion.dismiss", { lng: target, defaultValue: "Dismiss" })}
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[#5a5d77] hover:bg-black/5"
      >
        <Icon name="ri-close-line" className="text-lg" />
      </button>
    </div>
  );
}
